import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { GridApi } from 'ag-grid-community'
import { useDependencyContext } from './DependencyContext'
import { collectDependentWbsItemNames } from './collectDependentWbsItemNames'

type DragType = 'track' | 'start-thumb' | 'end-thumb'

type UseDependencyCascadeTooltipParams = {
  isDragging: boolean
  dragType: DragType | null
  wbsItemUuid: string | undefined
  api: GridApi
  dataPath: string
}

/**
 * MUI Tooltip の PopperProps.anchorEl に渡すための仮想要素。
 * getBoundingClientRect がマウス座標を返すことで、ツールチップがマウスに追従する。
 */
const createVirtualElement = () => {
  const pos = { x: 0, y: 0 }
  return {
    updatePosition: (x: number, y: number) => {
      pos.x = x
      pos.y = y
    },
    getBoundingClientRect: () => ({
      x: pos.x,
      y: pos.y,
      width: 0,
      height: 0,
      top: pos.y,
      right: pos.x,
      bottom: pos.y,
      left: pos.x,
      toJSON: () => JSON.stringify({ x: pos.x, y: pos.y }),
    }),
  }
}

/**
 * ドラッグ中の依存関係連動ツールチップのための情報を提供するフック。
 * Ctrl/Metaキーの押下状態と、連動対象のWBSアイテム一覧を返す。
 */
export const useDependencyCascadeTooltip = ({
  isDragging,
  dragType,
  wbsItemUuid,
  api,
  dataPath,
}: UseDependencyCascadeTooltipParams) => {
  const dependencyContext = useDependencyContext()
  const [isCtrlPressed, setIsCtrlPressed] = useState(false)
  const virtualElementRef = useRef(createVirtualElement())

  // Tooltip の Popper 再計算をトリガーするためのカウンター
  const [, setPositionTick] = useState(0)

  const triggerReposition = useCallback(() => {
    setPositionTick(prev => prev + 1)
  }, [])

  // Ctrl/Metaキーの監視 + マウス位置のトラッキング
  // mousemove: ドラッグ中の継続的な修飾キー状態の検知（常に正確）+ 位置追従
  // keydown/keyup: マウス静止中の即時応答
  useEffect(() => {
    if (!isDragging) {
      setIsCtrlPressed(false)
      return
    }

    const handleMouseMove = (e: MouseEvent) => {
      setIsCtrlPressed(e.ctrlKey || e.metaKey)
      virtualElementRef.current.updatePosition(e.clientX, e.clientY)
      triggerReposition()
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Control' || e.key === 'Meta') {
        setIsCtrlPressed(true)
      }
    }
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Control' || e.key === 'Meta') {
        setIsCtrlPressed(false)
      }
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('keyup', handleKeyUp)
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('keyup', handleKeyUp)
    }
  }, [isDragging, triggerReposition])

  // 依存アイテム一覧をドラッグ開始時に1回だけ計算
  const dependentItems = useMemo(() => {
    if (!isDragging || !dragType || !dependencyContext || !wbsItemUuid) {
      return []
    }

    // dragTypeに応じてBFS走査方向を決定
    const directions: ('forward' | 'backward')[] =
      dragType === 'track'
        ? ['forward', 'backward']
        : dragType === 'start-thumb'
          ? ['backward']
          : ['forward']

    return collectDependentWbsItemNames({
      sourceWbsItemUuids: new Set([wbsItemUuid]),
      directions,
      dependencies: dependencyContext.dependencies,
      wbsItemUuidToRowIndexMap: dependencyContext.getWbsItemUuidToRowIndexMap(),
      api,
      dataPath,
      wbsItemInfoMap: dependencyContext.wbsItemInfoMap,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ドラッグ開始時に1回だけ計算する
  }, [isDragging, dragType])

  // mousedown 時点でマウス座標を仮想要素に設定する。
  // useEffect の mousemove リスナーはレンダー後に登録されるため、
  // ドラッグ開始直後のレンダーでは仮想要素が初期位置 (0,0) のまま。
  // placement="top" だとツールチップがビューポート外に配置されてしまう。
  const initPosition = useCallback((x: number, y: number) => {
    virtualElementRef.current.updatePosition(x, y)
  }, [])

  return {
    isCtrlPressed,
    dependentItems,
    hasDependents: dependentItems.length > 0,
    tooltipAnchorEl: virtualElementRef.current,
    initPosition,
  }
}
